import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatPrice } from '../../../utils/formatters';
import BundlePreviewModal from './BundlePreviewModal';

const BundleSuggestions = ({ suggestions, onAddBundle, onAddItem }) => {
  const [previewBundle, setPreviewBundle] = useState(null);

  if (!suggestions || suggestions?.length === 0) return null;

  return (
    <>
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="Lightbulb" size={20} className="text-accent" />
          <h3 className="text-lg font-semibold text-foreground">Recommended Bundles</h3>
        </div>
        <div className="space-y-4">
          {suggestions?.map((bundle) => (
            <div key={bundle?.id} className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-smooth cursor-pointer" onClick={() => setPreviewBundle(bundle)}>
              <div className="flex items-start space-x-4">
                {/* Bundle Preview */}
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-muted rounded-lg overflow-hidden">
                    <Image
                      src={bundle?.image}
                      alt={bundle?.imageAlt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Bundle Info */}
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">
                        {bundle?.name}
                      </h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        {bundle?.itemCount} items • {bundle?.category}
                      </p>
                      {/* Store Name */}
                      <div className="flex items-center gap-1 mt-1 text-[10px] text-muted-foreground">
                        <Icon name="Store" size={10} />
                        <span>{bundle?.sellerBusinessName || 'OpenGadget'}</span>
                      </div>
                      
                      {/* Savings Badge */}
                      {bundle?.savings > 0 && (
                        <div className="inline-flex items-center space-x-1 mt-2 px-2 py-1 bg-success/10 text-success rounded-full">
                          <Icon name="TrendingDown" size={12} />
                          <span className="text-xs font-medium">
                            Save {formatPrice(bundle?.savings)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Pricing */}
                    <div className="text-right">
                      <div className="text-sm font-bold text-foreground">
                        {formatPrice(bundle?.bundlePrice)}
                      </div>
                      {bundle?.originalPrice > bundle?.bundlePrice && (
                        <div className="text-xs text-muted-foreground line-through">
                          {formatPrice(bundle?.originalPrice)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Bundle Items Preview */}
                  <div className="flex items-center space-x-2 mt-3 cursor-pointer hover:opacity-80 transition-opacity" onClick={(e) => { e.stopPropagation(); setPreviewBundle(bundle); }} title="Click to preview items">
                    <span className="text-xs text-muted-foreground">Includes:</span>
                    <div className="flex items-center space-x-1">
                      {bundle?.items?.slice(0, 3)?.map((item, index) => (
                        <div key={index} className="w-6 h-6 bg-muted rounded overflow-hidden">
                          <Image
                            src={item?.thumbnail}
                            alt={item?.thumbnailAlt}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                      {bundle?.items?.length > 3 && (
                        <div className="w-6 h-6 bg-muted rounded flex items-center justify-center">
                          <span className="text-xs text-muted-foreground">
                            +{bundle?.items?.length - 3}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2 mt-4" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={(e) => { e.stopPropagation(); onAddBundle(bundle?.id); }}
                    >
                      <Icon name="Package" size={14} />
                      <span className="ml-1">Add Bundle to Shopping Cart</span>
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={(e) => { e.stopPropagation(); setPreviewBundle(bundle); }}
                    >
                      <Icon name="Eye" size={14} />
                      <span className="ml-1">Details</span>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Individual Suggestions */}
        {suggestions?.some(s => s?.type === 'individual') && (
          <div className="mt-6 pt-6 border-t border-border">
            <h4 className="text-sm font-medium text-foreground mb-3">
              You might also like
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {suggestions?.filter(s => s?.type === 'individual')?.slice(0, 6)?.map((item) => (
                  <div key={item?.id} className="border border-border rounded-lg p-3 hover:bg-muted/50 transition-smooth">
                    <div className="w-full h-20 bg-muted rounded overflow-hidden mb-2">
                      <Image
                        src={item?.image}
                        alt={item?.imageAlt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h5 className="text-xs font-medium text-foreground truncate">
                      {item?.name}
                    </h5>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs font-semibold text-foreground">
                        {formatPrice(item?.price)}
                      </span>
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => onAddItem(item?.id)}
                      >
                        <Icon name="Plus" size={12} />
                      </Button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewBundle && (
        <BundlePreviewModal 
            bundle={previewBundle} 
            onClose={() => setPreviewBundle(null)} 
            onAddBundle={(id) => {
                onAddBundle(id);
                setPreviewBundle(null);
            }} 
        />
      )}
    </>
  );
};

export default BundleSuggestions;
